import type { ButtonVariant } from '@sds-system/components';
import type { CSSObject, SizeKey, Theme } from '@sds-system/types';
import { getColorValue, processNestedCSS } from '@sds-system/utils';

export const buttonVariant: (theme: Theme) => Record<ButtonVariant, CSSObject> = (
  theme: Theme,
) => ({
  'solid.primary': {
    color: theme.palette.system.white,
    backgroundColor: theme.palette.bg.interactive.primary,
    border: `1px solid transparent`,

    ':hover': {
      backgroundColor: getColorValue(theme.palette.bg.interactive.primaryHover, theme),
    },
    ':active': {
      backgroundColor: getColorValue(theme.palette.bg.interactive.primaryPressed, theme),
    },
    ':disabled': {
      backgroundColor: getColorValue(theme.palette.bg.disabledDark, theme),
    },
  },
  'solid.info': {
    color: theme.palette.system.white,
    backgroundColor: getColorValue(theme.palette.bg.interactive.info, theme),
    border: `1px solid transparent`,

    ':hover': {
      backgroundColor: getColorValue(theme.palette.bg.interactive.infoHover, theme),
    },
    ':active': {
      backgroundColor: getColorValue(theme.palette.bg.interactive.infoPressed, theme),
    },
    ':disabled': {
      backgroundColor: getColorValue(theme.palette.bg.disabledDark, theme),
    },
  },
  'solid.error': {
    color: theme.palette.system.white,
    backgroundColor: getColorValue(theme.palette.bg.interactive.error, theme),
    border: `1px solid transparent`,

    ':hover': {
      backgroundColor: getColorValue(theme.palette.bg.interactive.errorHover, theme),
    },
    ':active': {
      backgroundColor: getColorValue(theme.palette.bg.interactive.errorPressed, theme),
    },
    ':disabled': {
      backgroundColor: getColorValue(theme.palette.bg.disabledDark, theme),
    },
  },

  'secondarySolid.info': {
    color: theme.palette.text.info,
    backgroundColor: getColorValue(theme.palette.bg.infoSubtle, theme),
    border: `1px solid transparent`,

    ':hover': processNestedCSS(
      {
        color: theme.palette.text.interactive.infoHover,
        border: `1px solid ${theme.palette.border.interactive.infoHover}`,
      },
      theme,
    ),
    ':active': processNestedCSS(
      {
        color: theme.palette.text.interactive.infoPressed,
        border: `1px solid ${theme.palette.border.interactive.infoPressed}`,
      },
      theme,
    ),
    ':disabled': {
      backgroundColor: getColorValue(theme.palette.bg.disabledLight, theme),
      color: getColorValue(theme.palette.text.disabled, theme),
    },
  },
  'secondarySolid.error': {
    color: theme.palette.text.error,
    backgroundColor: getColorValue(theme.palette.bg.errorSubtle, theme),
    border: `1px solid transparent`,

    ':hover': processNestedCSS(
      {
        color: theme.palette.text.interactive.errorHover,
        border: `1px solid ${theme.palette.border.interactive.errorHover}`,
      },
      theme,
    ),
    ':active': processNestedCSS(
      {
        color: theme.palette.text.interactive.errorPressed,
        border: `1px solid ${theme.palette.border.interactive.errorPressed}`,
      },
      theme,
    ),
    ':disabled': {
      backgroundColor: getColorValue(theme.palette.bg.disabledLight, theme),
      color: getColorValue(theme.palette.text.disabled, theme),
    },
  },

  'outline.primary': {
    backgroundColor: theme.palette.system.white,
    color: getColorValue(theme.palette.text.interactive.primary, theme),
    border: `1px solid ${getColorValue(theme.palette.border.interactive.primary, theme)}`,

    ':hover': {
      color: getColorValue(theme.palette.text.interactive.primaryHover, theme),
      border: `1px solid ${getColorValue(theme.palette.border.interactive.primaryHover, theme)}`,
    },
    ':active': {
      color: getColorValue(theme.palette.text.interactive.primaryPressed, theme),
      border: `1px solid ${getColorValue(theme.palette.border.interactive.primaryPressed, theme)}`,
    },
    ':disabled': {
      color: getColorValue(theme.palette.text.disabled, theme),
      backgroundColor: getColorValue(theme.palette.bg.disabledLight, theme),
      border: `1px solid ${getColorValue(theme.palette.border.disabled, theme)}`,
    },
  },
  'outline.assistive': {
    backgroundColor: theme.palette.system.white,
    color: getColorValue(theme.palette.text.interactive.secondary, theme),
    border: `1px solid ${getColorValue(theme.palette.border.interactive.secondary, theme)}`,

    ':hover': {
      color: getColorValue(theme.palette.text.interactive.secondaryHover, theme),
      border: `1px solid ${getColorValue(theme.palette.border.interactive.primary, theme)}`,
    },
    ':active': {
      color: getColorValue(theme.palette.text.interactive.secondaryHover, theme),
      backgroundColor: getColorValue(theme.palette.bg.interactive.selected, theme),
      border: `1px solid ${getColorValue(theme.palette.border.interactive.primary, theme)}`,
    },
    ':disabled': {
      color: getColorValue(theme.palette.text.disabled, theme),
      backgroundColor: getColorValue(theme.palette.bg.disabledLight, theme),
      border: `1px solid ${getColorValue(theme.palette.border.disabled, theme)}`,
    },
  },
  'outline.info': {
    backgroundColor: theme.palette.system.white,
    color: theme.palette.text.info,
    border: `1px solid ${theme.palette.border.info}`,

    ':hover': processNestedCSS(
      {
        color: theme.palette.text.interactive.infoHover,
        border: `1px solid ${theme.palette.border.interactive.infoHover}`,
      },
      theme,
    ),
    ':active': processNestedCSS(
      {
        color: theme.palette.text.interactive.infoPressed,
        border: `1px solid ${theme.palette.border.interactive.infoPressed}`,
      },
      theme,
    ),
    ':disabled': {
      color: getColorValue(theme.palette.text.disabled, theme),
      backgroundColor: getColorValue(theme.palette.bg.disabledLight, theme),
      border: `1px solid ${getColorValue(theme.palette.border.disabled, theme)}`,
    },
  },
  'outline.error': {
    backgroundColor: theme.palette.system.white,
    color: theme.palette.text.error,
    border: `1px solid ${theme.palette.border.error}`,

    ':hover': processNestedCSS(
      {
        color: theme.palette.text.interactive.errorHover,
        border: `1px solid ${theme.palette.border.interactive.errorHover}`,
      },
      theme,
    ),
    ':active': processNestedCSS(
      {
        color: theme.palette.text.interactive.errorPressed,
        border: `1px solid ${theme.palette.border.interactive.errorPressed}`,
      },
      theme,
    ),
    ':disabled': {
      color: getColorValue(theme.palette.text.disabled, theme),
      backgroundColor: getColorValue(theme.palette.bg.disabledLight, theme),
      border: `1px solid ${getColorValue(theme.palette.border.disabled, theme)}`,
    },
  },

  'ghost.primary': {
    color: getColorValue(theme.palette.text.interactive.primary, theme),
    backgroundColor: 'transparent',
    border: `1px solid transparent`,

    ':hover': {
      color: getColorValue(theme.palette.text.interactive.primaryHover, theme),
    },
    ':active': {
      color: getColorValue(theme.palette.text.interactive.primaryPressed, theme),
    },
    ':disabled': {
      color: getColorValue(theme.palette.text.disabled, theme),
    },
  },
  'ghost.assistive': {
    color: getColorValue(theme.palette.text.interactive.secondary, theme),
    backgroundColor: 'transparent',
    border: `1px solid transparent`,

    ':hover': {
      color: getColorValue(theme.palette.text.interactive.secondaryHover, theme),
    },
    ':active': {
      color: getColorValue(theme.palette.text.interactive.secondaryPressed, theme),
    },
    ':disabled': {
      color: getColorValue(theme.palette.text.disabled, theme),
    },
  },
  'ghost.info': {
    color: getColorValue(theme.palette.text.interactive.info, theme),
    backgroundColor: 'transparent',
    border: `1px solid transparent`,

    ':hover': {
      color: getColorValue(theme.palette.text.interactive.infoHover, theme),
    },
    ':active': {
      color: getColorValue(theme.palette.text.interactive.infoPressed, theme),
    },
    ':disabled': {
      color: getColorValue(theme.palette.text.disabled, theme),
    },
  },
  'ghost.error': {
    color: getColorValue(theme.palette.text.interactive.error, theme),
    backgroundColor: 'transparent',
    border: `1px solid transparent`,

    ':hover': {
      color: getColorValue(theme.palette.text.interactive.errorHover, theme),
    },
    ':active': {
      color: getColorValue(theme.palette.text.interactive.errorPressed, theme),
    },
    ':disabled': {
      color: getColorValue(theme.palette.text.disabled, theme),
    },
  },
});

export const heightStyle: Record<Exclude<SizeKey, '2XS' | '3XS' | '3XL' | '4XL'>, CSSObject> = {
  XS: {
    paddingLeft: 4,
    paddingRight: 4,
  },
  S: {
    paddingLeft: 8,
    paddingRight: 8,
  },
  M: {
    paddingLeft: 12,
    paddingRight: 12,
  },
  L: {
    paddingLeft: 12,
    paddingRight: 12,
  },
  XL: {
    paddingLeft: 16,
    paddingRight: 16,
  },
  '2XL': {
    paddingLeft: 20,
    paddingRight: 20,
  },
};

export const roundStyle: Record<Exclude<SizeKey, '2XS' | '3XS' | '3XL' | '4XL'>, CSSObject> = {
  XS: {
    borderRadius: 4,
  },
  S: {
    borderRadius: 8,
  },
  M: {
    borderRadius: 8,
  },
  L: {
    borderRadius: 8,
  },
  XL: {
    borderRadius: 8,
  },
  '2XL': {
    borderRadius: 8,
  },
};
